import React from "react";
import "../styles/PrivacyPolicyPage.css";

const sections = [
  {
    title: "1. Information We Collect",
    body: [
      "We aim to collect only what we reasonably need to run, protect, and improve IDless.",
    ],
    groups: [
      {
        label: "Information you provide",
        items: [
          "Basic account or profile details, such as a display name.",
          "Messages, posts, reports, and other content you choose to send or share through IDless.",
          "Communications you send to us, including support requests and content reports.",
        ],
      },
      {
        label: "Information collected automatically",
        items: [
          "Device and app information, such as device type, operating system, app version, identifiers, and crash logs.",
          "Usage information, such as features used, session length, general interaction data, and performance signals.",
        ],
      },
      {
        label: "Information from others",
        items: [
          "Reports, blocks, or flags submitted by other users about accounts or content, including reports connected to our objectionable content rules.",
        ],
      },
    ],
    closing:
      "We do not knowingly collect more information than is needed to provide, secure, maintain, and improve IDless.",
  },
  {
    title: "2. How We Use Information",
    body: ["We use information to:"],
    items: [
      "Provide and maintain the core IDless experience, including chat and messaging.",
      "Keep the service safer by reviewing reports, blocking abusive users, and enforcing our no-tolerance policy for objectionable content.",
      "Detect, investigate, and prevent fraud, abuse, unauthorized access, harmful activity, and illegal activity.",
      "Improve reliability, performance, security, and the overall user experience.",
      "Respond to support requests and communicate with you about IDless.",
      "Comply with legal obligations and enforce our terms and agreements.",
    ],
    closing:
      "When we use information in aggregated or de-identified form, it is not used to personally identify you.",
  },
  {
    title: "3. How We Share Information",
    body: [
      "We do not sell your personal information. We may share information only in the following situations:",
    ],
    items: [
      "With other users, when sharing is part of how IDless works. For example, messages you send are visible to their recipients.",
      "With service providers who help us operate IDless, such as hosting, analytics, security, moderation, and crash reporting providers.",
      "For legal and safety reasons, if required by law, regulation, legal process, or a government request, or when needed to protect IDless, our users, or the public.",
      "In connection with a business transaction, such as a merger, acquisition, financing, reorganization, or sale of assets.",
      "With your consent, or for another purpose clearly explained to you at the time.",
    ],
    closing:
      "IDless is not responsible for the privacy practices of other users or third-party services you may access outside IDless.",
  },
  {
    title: "4. User Content and Chat Data",
    body: [
      "IDless is built around chat, so there are a few practical things to understand:",
    ],
    items: [
      "Messages and content you send may be stored and transmitted so the service can work.",
      "Some content may be kept for moderation, safety, security, support, and legal compliance purposes, even if it is no longer visible on your device.",
      "Content reported under our objectionable content rules may be reviewed by IDless personnel or automated systems.",
      "You are responsible for the content you choose to share. IDless does not pre-screen every message or piece of user content.",
    ],
  },
  {
    title: "5. Children's Privacy",
    body: [
      "IDless is intended for people who are 18 or older. Use by anyone under 18 requires verifiable permission from a parent or legal guardian.",
      "We do not knowingly collect personal information from children without the required consent. If we learn that we have collected personal information from a child without appropriate consent, we will take reasonable steps to delete it and may terminate the related account.",
      "Parents or guardians who believe their child has provided information to us without consent can contact us using the details below.",
    ],
  },
  {
    title: "6. Data Retention",
    body: [
      "We keep information only for as long as reasonably necessary to provide IDless, protect users, comply with legal obligations, resolve disputes, enforce our agreements, and support the purposes described in this Policy.",
      "Retention periods can vary depending on the type of information and why it is used.",
    ],
  },
  {
    title: "7. Data Security",
    body: [
      "We use commercially reasonable safeguards designed to protect information from unauthorized access, use, alteration, and disclosure.",
      "No system is perfectly secure, and we cannot guarantee absolute security. Please use care when deciding what to share through any online service.",
    ],
  },
  {
    title: "8. Your Choices and Rights",
    body: [
      "Depending on where you live, you may have rights to access, correct, delete, restrict, or object to certain uses of your personal information. To exercise these rights, contact us using the details below. We will respond as required by applicable law.",
      "IDless is designed to avoid unnecessary identification. User-generated content that is visible to other users is generally deleted within a day, or sooner where required.",
    ],
    items: [
      "You can use in-app blocking and reporting tools for objectionable content or users.",
      "You can request account deletion, subject to the retention needs described in this Policy.",
      "You can disable certain device permissions, such as Bluetooth, through your device settings.",
    ],
  },
  {
    title: "9. International Users",
    body: [
      "If you use IDless from outside the country where our servers or service providers are located, your information may be transferred to, stored in, and processed in another country.",
      "By using IDless, you understand that this transfer may happen where permitted by applicable law.",
    ],
  },
  {
    title: "10. Changes to This Policy",
    body: [
      "We may update this Privacy Policy from time to time. When we do, we will update the date at the top of this page. If a change is material and the law requires additional notice, we will provide it.",
      "Your continued use of IDless after changes take effect means you accept the updated Policy.",
    ],
  },
];

const PrivacyPolicyPage = () => (
  <main className="privacy-policy-page">
    <article className="privacy-policy">
      <header className="privacy-policy__header">
        <img
          className="privacy-policy__logo"
          src="/idless_logo.png"
          alt="IDless"
        />
        <p className="privacy-policy__eyebrow">Privacy Policy</p>
        <h1>IDless Privacy Policy</h1>
        <p className="privacy-policy__updated">Last updated: 31-07-2026</p>
        <p className="privacy-policy__intro">
          This Privacy Policy explains how IDless ("IDless," "we," "us," or
          "our") collects, uses, shares, and protects information in connection
          with the IDless app, website, and chat service.
        </p>
        <p className="privacy-policy__intro">
          By installing, accessing, or using IDless, you acknowledge that you
          have read and understood this Privacy Policy. If you do not agree with
          it, you should not use IDless.
        </p>
      </header>

      <div className="privacy-policy__content">
        {sections.map((section) => (
          <section className="privacy-policy__section" key={section.title}>
            <h2>{section.title}</h2>
            {section.body?.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}

            {section.groups?.map((group) => (
              <div className="privacy-policy__group" key={group.label}>
                <h3>{group.label}</h3>
                <ul>
                  {group.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}

            {section.items && (
              <ul>
                {section.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}

            {section.closing && <p>{section.closing}</p>}
          </section>
        ))}

        <section className="privacy-policy__section privacy-policy__contact">
          <h2>11. Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy or want to exercise
            your privacy rights, contact us at{" "}
            <a href="mailto:support@idless.org">support@idless.org</a>.
          </p>
        </section>
      </div>
    </article>
  </main>
);

export default PrivacyPolicyPage;
